import React, { useEffect, useMemo, useState } from "react";
import { BotonVolver } from "@/components/BotonVolver";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { hoyISO } from "@/lib/zona";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { PERIODOS, rangoPeriodo, rangoMes, mesMas, resumir, catGasto, eur, fechaGasto, asociadoDe } from "@/lib/gastos";
import { RegistrarGasto } from "@/components/gastos/RegistrarGasto";
import { GastoDetalle, Asociados } from "@/components/gastos/GastoDetalle";
import { ResumenAcciones } from "@/components/gastos/ResumenAcciones";
import { SelectorPersona, ResumenPersonas, AsignarRapido, dePersona, SIN_ASIGNAR } from "@/components/gastos/PorPersona";
import { IngresoForm } from "@/components/gastos/IngresoForm";
import { Sheet } from "@/pages/Familia";

const QueAnotar = ({ onGasto, onIngreso, testid }) => (
  <div className="grid grid-cols-2 gap-2.5" data-testid={testid}>
    <button data-testid={`${testid}-gasto`} onClick={onGasto}
      className="min-h-[88px] rounded-2xl bg-[#3A6B56] text-white font-bold text-lg flex flex-col items-center justify-center gap-1 shadow-md hover:bg-[#2E5645]">
      <Icons.Camera size={26} /> Un gasto
    </button>
    <button data-testid={`${testid}-ingreso`} onClick={onIngreso}
      className="min-h-[88px] rounded-2xl bg-white border-2 border-[#3A6B56] text-[#3A6B56] font-bold text-lg flex flex-col items-center justify-center gap-0.5 hover:bg-[#F2F6F4]">
      <Icons.PiggyBank size={26} /> Un ingreso
      <span className="text-xs font-semibold text-[#4A5550]">Se anota a mano</span>
    </button>
  </div>
);

const FilaIngreso = ({ i, persona, onClick }) => (
  <button data-testid={`ingreso-${i.id}`} onClick={onClick} className="w-full text-left bg-white border border-[#E5DFD5] rounded-2xl hover:border-[#3A6B56] px-4 py-3.5 flex items-center gap-3">
    <span className="w-11 h-11 rounded-xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><Icons.PiggyBank size={22} className="text-[#3A6B56]" /></span>
    <span className="flex-1 min-w-0">
      <span className="block text-base font-semibold text-[#1C2421] truncate">{i.concepto}</span>
      <span className="block text-sm text-[#4A5550] truncate">{fechaGasto(i.fecha)}{persona ? ` · ${persona}` : ""}</span>
    </span>
    <span className="font-heading font-bold text-lg text-[#2E5645] shrink-0">+{eur(i.importe)}</span>
  </button>
);

const Balance = ({ ingresos, gastos }) => {
  const neto = ingresos - gastos;
  return (
    <div className="mt-4 pt-4 border-t border-[#EFEAE1] space-y-1.5 text-base" data-testid="balance">
      <div className="flex justify-between"><span className="text-[#4A5550]">Ingresos</span><span className="font-bold text-[#2E5645]" data-testid="balance-ingresos">{eur(ingresos)}</span></div>
      <div className="flex justify-between"><span className="text-[#4A5550]">Gastos</span><span className="font-bold text-[#1C2421]" data-testid="balance-gastos">{eur(gastos)}</span></div>
      <div className={`flex justify-between items-center rounded-xl px-3 py-2.5 mt-1 ${neto >= 0 ? "bg-[#E8F0EC] text-[#2E5645]" : "bg-[#FEF2F2] text-[#B91C1C]"}`}>
        <span className="font-semibold">Diferencia</span>
        <span className="font-heading text-2xl font-extrabold" data-testid="balance-neto">{neto > 0 ? "+" : ""}{eur(neto)}</span>
      </div>
    </div>
  );
};

const chip = (sel) => `min-h-[48px] px-4 rounded-full text-base font-semibold border-2 ${sel ? "bg-[#1C2421] border-[#1C2421] text-white" : "bg-white border-[#E5DFD5] text-[#1C2421]"}`;

const Desglose = ({ porCategoria, total }) => (
  <ul className="mt-4 space-y-2.5" data-testid="gastos-desglose">
    {porCategoria.map(([id, v]) => {
      const c = catGasto(id); const I = Icons[c.icon] || Icons.Receipt;
      return (
        <li key={id} data-testid={`gastos-cat-${id}`}>
          <div className="flex items-center gap-2 text-base"><I size={18} className="text-[#3A6B56] shrink-0" /><span className="flex-1 text-[#1C2421]">{c.label}</span><span className="font-bold text-[#1C2421]">{eur(v)}</span></div>
          <div className="h-1.5 rounded-full bg-[#EFEAE1] mt-1.5 overflow-hidden"><div className="h-full bg-[#3A6B56] rounded-full" style={{ width: `${total ? Math.max(3, (v / total) * 100) : 0}%` }} /></div>
        </li>
      );
    })}
  </ul>
);

const FilaGasto = ({ g, conTicket, onClick, sinAsignar, asignar }) => {
  const c = catGasto(g.categoria); const I = Icons[c.icon] || Icons.Receipt;
  return (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl hover:border-[#3A6B56]">
    <button data-testid={`gasto-${g.id}`} onClick={onClick} className="w-full text-left px-4 py-3.5 flex items-center gap-3">
      <span className="w-11 h-11 rounded-xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><I size={22} className="text-[#3A6B56]" /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-base font-semibold text-[#1C2421] truncate">{g.concepto || g.proveedor || c.label}</span>
        <span className="block text-sm text-[#4A5550] truncate">{fechaGasto(g.fecha)}{g.proveedor && g.concepto ? ` · ${g.proveedor}` : ""} · {c.label}</span>
        <span className="flex flex-wrap gap-x-3 mt-0.5"><Asociados g={g} />{sinAsignar && <span className="text-sm text-[#6E7A74]" data-testid={`gasto-sin-asignar-${g.id}`}>Sin asignar</span>}</span>
      </span>
      <span className="text-right shrink-0">
        <span className="block font-heading font-bold text-lg text-[#1C2421]">{eur(g.importe)}</span>
        {conTicket && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#3A6B56]"><Icons.Paperclip size={12} /> Ticket</span>}
      </span>
    </button>
    {asignar && <div className="px-4 pb-3.5 -mt-1"><AsignarRapido g={g} testid={`gasto-asignar-${g.id}`} /></div>}
    </div>
  );
};

export default function Gastos() {
  const data = useData();
  const { gastos = [], documents = [], ingresos = [] } = data;
  const [sp, setSp] = useSearchParams();
  const [periodo, setPeriodo] = useState("mes");
  const [custom, setCustom] = useState({});
  const mesHoy = hoyISO().slice(0, 7);
  const [mesSel, setMesSel] = useState(mesHoy);
  const [filtro, setFiltro] = useState("");
  const [persona, setPersona] = useState("");
  const [registrar, setRegistrar] = useState(false);
  const [abierto, setAbierto] = useState(null);
  const [anotar, setAnotar] = useState(false);
  const [ingresoForm, setIngresoForm] = useState(null);
  useEffect(() => {
    if (sp.get("registrar")) { setRegistrar(true); setSp({}, { replace: true }); }
    if (sp.get("anotar")) { setAnotar(true); setSp({}, { replace: true }); }
  }, [sp, setSp]);

  const rango = periodo === "mes" ? rangoMes(mesSel) : rangoPeriodo(periodo, custom);
  // Navegación mensual: desde el mes del gasto más antiguo hasta el más reciente (como mínimo, el mes actual).
  const meses = [...gastos, ...ingresos].map((x) => (x.fecha || "").slice(0, 7)).filter(Boolean);
  const mesMin = [mesHoy, ...meses].sort()[0];
  const mesMax = [mesHoy, ...meses].sort().slice(-1)[0];
  const activos = useMemo(() => new Set(documents.map((d) => d.id)), [documents]);
  const opcionesFiltro = useMemo(() => {
    const m = new Map();
    gastos.forEach((g) => asociadoDe(g, data).filter((a) => !a.persona).forEach((a) => m.set(a.key, a.label)));
    return [...m].sort((a, b) => a[1].localeCompare(b[1], "es"));
  }, [gastos, data]);
  const delPeriodo = gastos.filter((g) => g.fecha >= rango.desde && g.fecha <= rango.hasta && (!filtro || asociadoDe(g, data).some((a) => a.key === filtro)));
  const enPeriodo = delPeriodo.filter((g) => dePersona(g, persona));
  const nombrePersona = persona === SIN_ASIGNAR ? "Sin asignar" : (data.familyMembers || []).find((p) => p.id === persona)?.name;
  const conPersona = (id) => (data.familyMembers || []).some((p) => p.id === id);
  const { total, porCategoria } = resumir(enPeriodo);
  // Los ingresos solo se filtran por persona; con el filtro «¿De quién o de qué?» no aplican.
  const ingPeriodo = filtro ? [] : ingresos.filter((i) => i.fecha >= rango.desde && i.fecha <= rango.hasta && dePersona(i, persona));
  const totalIng = ingPeriodo.reduce((s, i) => s + i.importe, 0);
  const nombreDeId = (id) => (data.familyMembers || []).find((p) => p.id === id)?.name;
  const ing = ingresoForm && ingresoForm !== "nuevo" && ingresos.find((x) => x.id === ingresoForm);
  const mostrarIngreso = (x) => {
    setIngresoForm(null);
    if (!x?.fecha || (x.fecha >= rango.desde && x.fecha <= rango.hasta)) return;
    setPeriodo("mes"); setMesSel(x.fecha.slice(0, 7));
    toast(`Ingreso del ${fechaGasto(x.fecha)}: te muestro ${rangoMes(x.fecha.slice(0, 7)).label.toLowerCase()}.`, { duration: 6000 });
  };
  const nTickets = enPeriodo.filter((g) => g.documento_id && activos.has(g.documento_id)).length;
  const filtroLabel = opcionesFiltro.find(([k]) => k === filtro)?.[1];
  const g = abierto && gastos.find((x) => x.id === abierto);

  // Si el gasto guardado no entra en la vista actual, se cambia a su periodo y se avisa.
  const mostrarGuardado = (x) => {
    if (!x?.fecha) return;
    const fuera = x.fecha < rango.desde || x.fecha > rango.hasta;
    if (!dePersona(x, persona)) setPersona("");
    if (filtro && !asociadoDe(x, data).some((a) => a.key === filtro)) setFiltro("");
    if (!fuera) return;
    setPeriodo("mes"); setMesSel(x.fecha.slice(0, 7));
    const label = rangoMes(x.fecha.slice(0, 7)).label;
    toast(`Gasto del ${fechaGasto(x.fecha)}: te muestro ${label.toLowerCase()}.`, { duration: 6000 });
  };

  return (
    <div className="space-y-5" data-testid="gastos-page">
      <BotonVolver porDefecto="/areas" testid="gastos-volver" />
      <header>
        <h1 className="font-heading text-3xl font-bold text-[#1C2421]">Ingresos y gastos</h1>
        <p className="text-base text-[#6E7A74] mt-1">Apunta lo que cobras y lo que pagas, y mira la diferencia.</p>
      </header>
      <section data-testid="gastos-que-anotar">
        <p className="font-heading text-xl font-bold text-[#1C2421] mb-2.5">¿Qué quieres anotar?</p>
        <QueAnotar testid="anotar" onGasto={() => setRegistrar(true)} onIngreso={() => setIngresoForm("nuevo")} />
      </section>

      <SelectorPersona value={persona} onChange={setPersona} />
      <div className="flex flex-wrap gap-2" role="tablist" data-testid="gastos-periodos">
        {PERIODOS.map(([id, l]) => <button key={id} role="tab" aria-selected={periodo === id && (id !== "mes" || mesSel === mesHoy)} data-testid={`gastos-periodo-${id}`} onClick={() => { setPeriodo(id); if (id === "mes") setMesSel(mesHoy); }} className={`${chip(periodo === id && (id !== "mes" || mesSel === mesHoy))} shrink-0`}>{l}</button>)}
      </div>
      <div className="flex items-center justify-between gap-2 bg-white border border-[#E5DFD5] rounded-2xl p-2" data-testid="gastos-nav-mes">
        <button data-testid="gastos-mes-anterior" aria-label="Mes anterior" disabled={periodo === "mes" && mesSel <= mesMin}
          onClick={() => { const base = periodo === "mes" ? mesSel : rango.desde.slice(0, 7); setPeriodo("mes"); setMesSel(mesMas(base, -1)); }}
          className="min-h-[48px] min-w-[48px] rounded-xl flex items-center justify-center text-[#1C2421] disabled:opacity-30"><Icons.ChevronLeft size={24} /></button>
        <p className="text-base font-bold text-[#1C2421] text-center" data-testid="gastos-mes-label">{periodo === "mes" ? rangoMes(mesSel).label : "Ver por meses"}</p>
        <button data-testid="gastos-mes-siguiente" aria-label="Mes siguiente" disabled={periodo === "mes" && mesSel >= mesMax}
          onClick={() => { const base = periodo === "mes" ? mesSel : rango.desde.slice(0, 7); setPeriodo("mes"); setMesSel(mesMas(base, 1)); }}
          className="min-h-[48px] min-w-[48px] rounded-xl flex items-center justify-center text-[#1C2421] disabled:opacity-30"><Icons.ChevronRight size={24} /></button>
      </div>
      {periodo === "personalizado" && (
        <div className="grid grid-cols-2 gap-3" data-testid="gastos-personalizado">
          {[["desde", "Desde"], ["hasta", "Hasta"]].map(([k, l]) => (
            <label key={k} className="block"><span className="text-sm font-semibold text-[#4A5550]">{l}</span>
              <input type="date" data-testid={`gastos-${k}`} value={custom[k] || rango[k]} onChange={(e) => setCustom((c) => ({ ...c, [k]: e.target.value }))}
                className="mt-1 w-full min-h-[52px] px-3 rounded-xl bg-white border border-[#E5DFD5] text-base" /></label>
          ))}
        </div>
      )}
      {opcionesFiltro.length > 0 && (
        <label className="block"><span className="text-sm font-semibold text-[#4A5550]">¿De quién o de qué? <span className="font-normal text-[#6E7A74]">(opcional)</span></span>
          <select data-testid="gastos-filtro" value={filtro} onChange={(e) => setFiltro(e.target.value)} className="mt-1 w-full min-h-[52px] px-3 rounded-xl bg-white border border-[#E5DFD5] text-base">
            <option value="">Cualquiera</option>
            {opcionesFiltro.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
      )}

      <section className="bg-white border border-[#E5DFD5] rounded-2xl p-5" data-testid="gastos-resumen">
        <p className="font-heading text-xl font-bold text-[#1C2421]" data-testid="gastos-persona-label">{nombrePersona ? `Resumen de ${nombrePersona === "Sin asignar" ? "sin asignar" : nombrePersona}` : "Resumen de todos"}</p>
        <p className="text-base font-semibold text-[#4A5550]" data-testid="gastos-periodo-label">{rango.label}{filtroLabel ? ` · ${filtroLabel}` : ""}</p>
        <p className="text-sm text-[#6E7A74] mt-2">Total gastado</p>
        <p className="font-heading text-4xl font-extrabold text-[#1C2421]" data-testid="gastos-total">{eur(total)}</p>
        <p className="text-sm text-[#4A5550] mt-1" data-testid="gastos-contador">{enPeriodo.length} {enPeriodo.length === 1 ? "gasto" : "gastos"} · {nTickets} con justificante</p>
        {!filtro && <Balance ingresos={totalIng} gastos={total} />}
        {!persona && <ResumenPersonas gastos={enPeriodo} onElegir={setPersona} />}
        {porCategoria.length > 0 && <Desglose porCategoria={porCategoria} total={total} />}
      </section>

      <ResumenAcciones rango={rango} filtro={filtroLabel ? `Asociado a: ${filtroLabel}` : null} persona={nombrePersona} porPersona={!persona}
        ids={filtro || persona ? enPeriodo.map((x) => x.id) : undefined} ingresoIds={filtro || persona ? ingPeriodo.map((x) => x.id) : undefined}
        vacio={!enPeriodo.length && !ingPeriodo.length} />

      {!filtro && (
        <section className="space-y-2.5" data-testid="ingresos-lista">
          <h2 className="font-heading text-xl font-bold text-[#1C2421]">Ingresos del periodo</h2>
          {ingPeriodo.length ? ingPeriodo.map((x) => <FilaIngreso key={x.id} i={x} persona={nombreDeId(x.family_member_id)} onClick={() => setIngresoForm(x.id)} />)
            : <p className="text-center text-[#6E7A74] py-6" data-testid="ingresos-vacio">No hay ingresos apuntados en este periodo.</p>}
          {ingPeriodo.length > 0 && (
            <div className="flex justify-between items-center rounded-2xl bg-[#E8F0EC] px-4 py-3.5" data-testid="ingresos-total">
              <span className="font-semibold text-[#2E5645]">Total ingresos{ingPeriodo.length > 1 ? ` (${ingPeriodo.length})` : ""}</span>
              <span className="font-heading text-xl font-extrabold text-[#2E5645]">{eur(totalIng)}</span>
            </div>
          )}
        </section>
      )}

      <section className="space-y-2.5" data-testid="gastos-lista">
        <h2 className="font-heading text-xl font-bold text-[#1C2421]">Gastos del periodo</h2>
        {enPeriodo.length ? enPeriodo.map((x) => <FilaGasto key={x.id} g={x} conTicket={!!x.documento_id && activos.has(x.documento_id)} onClick={() => setAbierto(x.id)}
          sinAsignar={!persona && !conPersona(x.family_member_id) && !x.pet_id && !x.vehiculo_id && !x.vivienda_id} asignar={persona === SIN_ASIGNAR} />)
          : <p className="text-center text-[#6E7A74] py-8" data-testid="gastos-vacio">{persona === SIN_ASIGNAR ? "No hay gastos sin asignar en este periodo." : "No hay gastos en este periodo."}</p>}
      </section>

      {registrar && <RegistrarGasto personaInicial={persona && persona !== SIN_ASIGNAR ? persona : ""} onClose={() => setRegistrar(false)} onGuardado={mostrarGuardado} />}
      {g && <GastoDetalle g={g} onClose={() => setAbierto(null)} onGuardado={mostrarGuardado} />}
      {ingresoForm && <IngresoForm ingreso={ing || null} personaInicial={persona && persona !== SIN_ASIGNAR ? persona : ""}
        onClose={() => setIngresoForm(null)} onSaved={mostrarIngreso} />}
      {anotar && (
        <Sheet onClose={() => setAnotar(false)} testid="anotar-elegir">
          <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12 mb-4">¿Qué quieres anotar?</h2>
          <QueAnotar testid="anotar-sheet" onGasto={() => { setAnotar(false); setRegistrar(true); }} onIngreso={() => { setAnotar(false); setIngresoForm("nuevo"); }} />
        </Sheet>
      )}
    </div>
  );
}
